import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@chakra-ui/react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

/**
 * useReviewerEvent
 *
 * Reviewer's view of a single shared event: full event content + owner
 * identity + the reviewer's own verdict. Mirrors `useReviewerCampaign`.
 */
export function useReviewerEvent(eventId) {
  const { user } = useAuth();
  const queryKey = user?.id && eventId
    ? ['reviewerEvent', user.id, eventId]
    : ['reviewerEvent', 'disabled'];

  return useQuery({
    queryKey,
    queryFn: async () => {
      const { data } = await api.get(`/api/event-reviews/events/${eventId}`);
      return data;
    },
    enabled: Boolean(user?.id && eventId),
    staleTime: 60 * 1000,
    retry: (failureCount, err) => {
      const statusCode = err?.response?.status;
      // 410 = owner reopened the event to edit it (link superseded).
      if (statusCode === 404 || statusCode === 403 || statusCode === 410) return false;
      return failureCount < 1;
    },
  });
}

/** Patch the matching summary in the "Shared with me" list cache by event_id. */
function patchSharedEventInCache(queryClient, userId, eventId, patch) {
  queryClient.setQueryData(['sharedEvents', userId], (old) => {
    if (!Array.isArray(old)) return old;
    return old.map((s) => (s.event_id === eventId ? { ...s, ...patch } : s));
  });
}

/**
 * Submit an approve / reject / changes-requested verdict for an event.
 *
 * Optimistic: the header chip and the "Shared with me" row flip the moment the
 * reviewer confirms, so the modal can close instantly. We reconcile from the
 * server's serialized event (`data.event`) on success and roll both caches back
 * on failure. The reviewer stays mounted on the page throughout, so the standard
 * `useMutation` lifecycle is sufficient (no app-scoped queryClient workaround).
 */
export function useEventVerdictMutation(eventId) {
  const { user } = useAuth();
  const userId = user?.id;
  const queryClient = useQueryClient();
  const toast = useToast();

  const eventKey = ['reviewerEvent', userId, eventId];
  const sharedKey = ['sharedEvents', userId];

  return useMutation({
    mutationFn: async ({ status, note }) => {
      const { data } = await api.post(
        `/api/event-reviews/events/${eventId}/verdict`,
        { status, note: note || null }
      );
      return data;
    },
    onMutate: async ({ status, note }) => {
      if (!userId) return {};
      await Promise.all([
        queryClient.cancelQueries({ queryKey: eventKey }),
        queryClient.cancelQueries({ queryKey: sharedKey }),
      ]);

      const prevEvent = queryClient.getQueryData(eventKey);
      const prevShared = queryClient.getQueryData(sharedKey);
      const decidedAt = new Date().toISOString();

      // Flip the single-event view (drives the header chip + verdict state).
      queryClient.setQueryData(eventKey, (old) =>
        old
          ? {
              ...old,
              complianceStatus: status,
              my_verdict: { status, note: note || null, decided_at: decidedAt },
            }
          : old,
      );
      // Flip the "Shared with me" row so it's consistent if the reviewer
      // navigates back to the list.
      patchSharedEventInCache(queryClient, userId, eventId, {
        compliance_status: status,
        verdict_at: decidedAt,
        verdict_note: note || null,
      });

      return { prevEvent, prevShared };
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.prevEvent !== undefined) {
        queryClient.setQueryData(eventKey, ctx.prevEvent);
      }
      if (ctx?.prevShared !== undefined) {
        queryClient.setQueryData(sharedKey, ctx.prevShared);
      }
      toast({
        title: "Couldn't submit your decision",
        description:
          err?.response?.data?.detail || err?.message || 'Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
    onSuccess: (data) => {
      if (!userId) return;
      // Reconcile the single-event view from server truth (no refetch).
      if (data?.event) {
        queryClient.setQueryData(eventKey, (old) =>
          old ? { ...old, ...data.event } : old,
        );
      }
      patchSharedEventInCache(queryClient, userId, eventId, {
        compliance_status: data?.verdict_status,
        verdict_at: data?.verdict_at,
        verdict_note: data?.verdict_note ?? null,
      });
    },
    // Cheap safety net for the list (single-event view is already authoritative
    // from data.event). Runs after the cache writes commit, so it can't revert.
    onSettled: () => {
      if (userId) queryClient.invalidateQueries({ queryKey: sharedKey });
    },
  });
}
